import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import { VStack, useToast } from "@chakra-ui/react";
import { AttemptsWarning } from "@equidefi/portals/components/AttemptsWarning";
import { MfaCodeInput } from "@equidefi/portals/components/MfaCodeInput";
import { ResendMfaLink } from "@equidefi/portals/components/ResendMfaLink";
import { useMFACode, useWorkflowLogin } from "@equidefi/portals/hooks/useAuthentication";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { investmentBcode, offeringIsCompleted } from "@equidefi/shared";
import { Heading, Text } from "@equidefi/ui";

import { WorkflowButton } from "@/components/workflow/WorkflowButton";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";
import { extractErrorMessage } from "@equidefi/portals";

import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowForm from "../WorkflowForm";

const WorkflowMFA = (props) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const { offering, analytics } = useWorkflowContext();
  const workflowLogin = useWorkflowLogin(offering?.id);
  const submitMFA = useMFACode();
  const [mfaCode, setMfaCode] = useState(searchParams.get("code"));
  const [isLoading, setIsLoading] = useState(false);
  const [cookies] = useCookies(["bcode", `ref_${offering?.slug}`]);
  const fromEmailContent = searchParams.get("email") && searchParams.get("code");

  const location = useLocation();

  useDocumentTitle("Multi-Factor Authentication");

  const onSubmit = async (e) => {
    e.preventDefault();
    const email = location.state?.email || searchParams.get("email");

    setIsLoading(true);
    try {
      const response = await submitMFA.mutateAsync({
        email,
        mfa_code: mfaCode,
        offering_id: location.state?.offering_id, // make sure offering_id is sent only after registration
        bcode: investmentBcode(cookies?.bcode),
        ref: cookies?.[`ref_${offering?.slug}`],
      });
      analytics.track("registered");
      if (offeringIsCompleted(offering)) {
        navigate("/vault/dashboard");
      } else if (response?.investment?.id) {
        navigate(`/offerings/${offering.slug}/${response.investment.id}/profile`);
        return;
      } else {
        navigate(`/offerings/${offering.slug}/choose`);
      }
    } catch (e) {
      console.error(e);
      toast({
        status: "error",
        description: extractErrorMessage(e, "Incorrect code, please try again"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (location.state && !location.state?.password && !location.state?.email)
      navigate(`/offerings/${offering.slug}/login`);
  }, [location, navigate]);

  return (
    <WorkflowContainer>
      <Heading textStyle="h2" fontSize="xl" fontWeight="700" m={0}>
        Two Factor Authentication
      </Heading>
      <Text textStyle="body2">
        Your account is protected with two-factor authentication. We've sent a code to {location.state?.email}. Please
        enter the code below.
      </Text>
      <AttemptsWarning error={submitMFA.error} />
      <WorkflowForm onSubmit={onSubmit}>
        <VStack>
          <MfaCodeInput value={mfaCode} onChange={setMfaCode} />
        </VStack>
        <WorkflowButton isDisabled={mfaCode?.length !== 6} isLoading={isLoading} onClick={onSubmit} mb={5}>
          Submit Code
        </WorkflowButton>
      </WorkflowForm>
      {!fromEmailContent && <ResendMfaLink alignSelf="center" authFunction={workflowLogin} state={location.state} />}
    </WorkflowContainer>
  );
};

export default WorkflowMFA;
